<template>
  <component :is="component" @click.prevent="handleDayClick" @keydown="handleDayKeydown" class="day--container"
     :class="dayClasses" :data-day-number="day.TITLE" :href="href"
     :data-day-offer="day.OFFER_CODE" :data-day-offer-strike="day.OFFER_CODE_STRIKE"
     :data-day-min-nights="day.MIN_NIGHTS" :aria-label="ariaLabel" :aria-hidden="ariaHidden">
    <span class="day--marker"></span>
    <span class="day--number">{{ day.TITLE }}</span>
    <span v-if="day.PRICE > 0" class="day--price">${{ day.PRICE }}</span>
  </component>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2024-05-17 (jhoward)
 *			- Refactor calendar day from hotelCalendar.vue to hotelCalendarDay.vue.
 */

import {defineComponent, PropType} from 'vue'

export interface CalendarDay {
  PRICE: number;
  BASE_PRICE: number;
  // HIGHLIGHT: boolean;
  // CHECK_IN: boolean;
  // CHECK_OUT: boolean;
  DISPLAY_RATES: boolean;
  OFFER_CODE: string;
  OFFER_CODE_STRIKE: string;
  ROOM_SEQUENCE_STRIKE: string;
  MIN_NIGHTS: number;
  TITLE: string;
  BLANK: boolean;
  KEY: string; //YYYY-MM-DD
  DATE: Date | null;
  BEFORE_TODAY: boolean | undefined;
}

export default defineComponent({
  name: 'hotelCalendarDay',
  emits: ['close'],
  props: {
    day: {
      type: Object as PropType<CalendarDay>,
      required: true,
    },
  },
  computed: {
    component(): string {
      return (!this.isBlank) ? 'a' : 'span';
    },
    isCheckIn(): boolean {
      return !!this.selectedDates.length && this.selectedDates[0] === this.day.KEY
    },
    isCheckOut(): boolean {
      return this.selectedDates.length > 1 && this.selectedDates[this.selectedDates.length - 1] === this.day.KEY
    },
    selectedDates(): string[] {
      return this.$store.getters.selectedDates;
    },
    ariaHidden(): string | null {
      return (this.day.BLANK) ? 'true' : null;
    },
    ariaLabel(): string | null {
      let output = null;
      if (!this.day.BLANK) {
        const suffix = (this.day.DATE) ? window.pepDateFunctions.dateSuffix(this.day.DATE?.getDate()) : '';
        output = this.getDayMonthName(this.day) + ' ' + this.day.TITLE + suffix;
        if (this.isCheckIn) {
          output += ', check in day';
        } else if (this.isCheckOut) {
          output += ', check out day';
        } else if (this.selectedDates.includes(this.day.KEY)) {
          const dayNumberOfReservation = this.selectedDates.indexOf(this.day.KEY) + 1;
          output += ', ' + dayNumberOfReservation + window.pepDateFunctions.dateSuffix(dayNumberOfReservation) + ' day of reservation';
        }
      }

      return output;
    },
    href(): string | null {
      return (this.isBlank) ? null : '#';
    },
    dayClasses(): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {
        'day--container-blank': this.isBlank,
        'day--container-check_in': this.isCheckIn,
        'day--container-check_out': this.isCheckOut,
        'day--container-highlight': this.isHighlighted,
        'day--container-no_rates': !this.day.DISPLAY_RATES,
      };

      if (!this.isBlank) {
        if (this.isNoData) {
          output['day--container-no_data'] = true;
        } else {
          if (this.isSpecialOffer) {
            output['day--container-special_offer'] = true;
          }
        }
      }

      return output;
    },
    isSpecialOffer(): boolean {
      return (this.day.DISPLAY_RATES && this.day.PRICE !== this.day.BASE_PRICE)
        || this.day.OFFER_CODE !== this.day.OFFER_CODE_STRIKE;
    },
    isBlank(): boolean {
      return this.day.BLANK || !!this.day.BEFORE_TODAY;
    },
    isHighlighted(): boolean {
      return this.selectedDates.includes(this.day.KEY);
    },
    isNoData(): boolean {
      return this.isBlank || (this.day.DISPLAY_RATES && this.day.PRICE === 0);
    },
  },
  methods: {
    getDayMonthName(day: CalendarDay): string {
      const month = day.DATE?.getMonth();
      if (month) {
        return window.pepDateFunctions.language.en.months[month];
      }

      return '';
    },

    handleDayKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleDayClick();
      } else if (event.key === 'Escape') {
        this.$emit('close');
      }
    },
    handleDayClick() {
      if (!this.isBlank) {
        if (this.day.DATE) {
          if (this.$store.state.checkInDate && this.$store.state.checkOutDate && !this.isNoData) {
            //both dates selected, start over
            this.$store.commit('updateCheckInDate', this.day.DATE);
            this.$store.commit('updateCheckOutDate', null);
          } else if (this.$store.state.checkInDate && !this.$store.state.checkOutDate) {
            //only check in selected
            if (this.day.DATE.getTime() < this.$store.state.checkInDate.getTime()) {
              this.$store.commit('updateCheckOutDate', this.$store.state.checkInDate);
              this.$store.commit('updateCheckInDate', this.day.DATE);
            } else {
              this.$store.commit('updateCheckOutDate', this.day.DATE);
            }

          } else if (!this.isNoData) {
            //fallback to setting up check in
            this.$store.commit('updateCheckInDate', this.day.DATE);
            this.$store.commit('updateCheckOutDate', null);
          }
        }
      }
    },
  }
})
</script>

<style scoped>
.day--container {
  background-color: #9e9e9e;
  color: #fff;
  cursor: pointer;
  padding: 0.5em 0.25em;
  position: relative;
  text-align: center;
  align-content: center;
  text-decoration: none;
  min-height: 44px;
}

.day--container:hover {
  color: #fff;
}

.ui-mobile .day--container {
  padding: 0.5em 0.375em;
}

.day--container-blank {
  cursor: default;
}

.day--container:first-child {
  border-left: none;
}

.day--container:last-child {
  border-right: none;
}

.day--price {
  white-space: nowrap;
}

.day--container-special_offer,
.day--container-special_offer.day--container-highlight.day--container-check_out {
  background: linear-gradient(to bottom, rgba(213, 34, 40, 1) 8%, rgba(158, 158, 158, 1) 8%, rgba(158, 158, 158, 1) 100%);
}

.no-touchevents .day--container:hover,
.no-touchevents .day--container.day--container-highlight.day--container-check_out:hover {
  background-color: rgba(158, 158, 158, 0.87);
}

.no-touchevents .day--container:hover + td.day--container-check_in .day-marker::before {
  border-left-color: #A9A9A9;
}

.no-touchevents .day--container:hover + td.day--container-check_out .day-marker::before {
  border-left-color: #1F8878;
}

.no-touchevents .day--container-special_offer:hover,
.no-touchevents .day--container-special_offer.day--container-highlight.day--container-check_out:hover {
  background: linear-gradient(to bottom, rgba(213, 34, 40, 1) 16%, rgba(158, 158, 158, 0.87) 16%, rgba(158, 158, 158, 0.87) 100%);
}

.no-touchevents .day--container-no_data:hover {
  background-color: #9E9E9E;
}

.no-touchevents .day--container-blank:hover {
  background-color: #EEE;
}

.day--marker {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 15px;
  height: 100%;
  overflow: hidden;
}

.day--container.day--container-check_in .day--marker,
.day--container.day--container-check_out .day--marker {
  display: block;
}

.day--container-special_offer + td.day--container-check_in .day--marker::after,
.day--container-special_offer.day--container-check_out .day--marker::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 8%;
  width: 100%;
  background-color: rgba(213, 34, 40, 1);
}

.no-touchevents .day--container-special_offer + .day--container-special_offer.day--container-check_in:hover .day--marker::after,
.no-touchevents .day--container-special_offer.day--container-check_out:hover .day--marker::after {
  height: 16%;
}

.day--container-check_in .day--marker::before,
.day--container-check_out .day--marker::before {
  content: '';
  border-bottom: 60px solid transparent;
  border-left: 15px solid #9E9E9E;
  border-top: 10px solid transparent;
  height: 0;
  left: 0;
  position: absolute;
  top: -10px;
  width: 0;
}

.day--container-check_out .day--marker::before {
  border-left-color: #007966;
}

.day--container-highlight {
  background-color: #007966;
  background-color: rgba(0, 121, 102, 1);
}

.day--container-highlight.day--container-check_out {
  background-color: #9E9E9E;
}

.day--container-highlight.day--container-special_offer {
  background: linear-gradient(to bottom, rgba(213, 34, 40, 1) 8%, rgba(0, 121, 102, 1) 8%, rgba(0, 121, 102, 1) 100%);
}

.no-touchevents .day--container-highlight:hover {
  background-color: rgba(0, 121, 102, 0.87);
}

.no-touchevents .day--container-highlight.day--container-special_offer:hover {
  background: linear-gradient(to bottom, rgba(213, 34, 40, 1) 16%, rgba(0, 121, 102, 0.87) 16%, rgba(0, 121, 102, 0.87) 100%);
}

.day--container a {

}

/*.day--container-no_rates a,
.day--container-no_rates div,
.day--container-no_rates span {
  font-size: 0;
}*/

.day--container-highlight a {
  color: #FFF;
}

.day--container-no_data {
  color: rgba(255, 255, 255, 0.54);
}

.day--container-blank {
  background-color: #EEE;
  border-color: #EEE;
}

.day--container a {
  display: block;
  color: #FFF;
}

.day--number {
  display: none;
}

.day--container[data-day-number]:not([data-day-number=""]) .day--number {
  display: block;
  font-size: 1.25em;
  position: relative;
}

.day--container-blank[data-day-number] .day--number {
  color: #000;
  color: rgba(0, 0, 0, 0.17);
}

.day--price {
  font-size: 0.8em;
}
</style>